'use client';

export type AttachmentStyle = 'avoidant' | 'anxious';
export type RelationshipContext = 'partner' | 'ex-partner' | 'work' | 'parent';
export type RelationshipIntention = 'end' | 'build-new' | 'improve';
export type InteractionType = 'sync' | 'async' | 'none';

export interface UserProfile {
  attachmentStyle: AttachmentStyle;
  relationshipContext: RelationshipContext;
  intention: RelationshipIntention;
  onboardingComplete: boolean;
  createdAt: string;
}

export interface InterventionSession {
  id: string;
  date: string;
  attachmentStyle: AttachmentStyle;
  relationshipContext: RelationshipContext;
  interactionType: InteractionType;
  actionChosen: 'default' | 'neutral' | 'values';
  actionText: string;
  reflectionQuestion: string;
  rating: number;
  durationSeconds: number;
}

const PROFILE_KEY = 'ract_profile';
const SESSIONS_KEY = 'ract_sessions';

export function getProfile(): UserProfile | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    return raw ? (JSON.parse(raw) as UserProfile) : null;
  } catch {
    return null;
  }
}

export function saveProfile(profile: UserProfile): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
}

export function getSessions(): InterventionSession[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(SESSIONS_KEY);
    return raw ? (JSON.parse(raw) as InterventionSession[]) : [];
  } catch {
    return [];
  }
}

export function saveSession(session: InterventionSession): void {
  if (typeof window === 'undefined') return;
  const sessions = getSessions();
  sessions.unshift(session);
  localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
}

export function clearAllData(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(PROFILE_KEY);
  localStorage.removeItem(SESSIONS_KEY);
}

export function exportData(): string {
  const profile = getProfile();
  const sessions = getSessions();
  return JSON.stringify({ profile, sessions, exportedAt: new Date().toISOString() }, null, 2);
}

// Stats helpers
export function computeStreak(sessions: InterventionSession[]): number {
  if (sessions.length === 0) return 0;
  const dates = [...new Set(sessions.map(s => s.date.split('T')[0]))].sort((a, b) => b.localeCompare(a));
  const today = new Date().toISOString().split('T')[0];
  let streak = 0;
  let current = today;
  for (const date of dates) {
    if (date === current) {
      streak++;
      const d = new Date(current);
      d.setDate(d.getDate() - 1);
      current = d.toISOString().split('T')[0];
    } else if (date < current) {
      break;
    }
  }
  return streak;
}

export function computeBestStreak(sessions: InterventionSession[]): number {
  if (sessions.length === 0) return 0;
  const dates = [...new Set(sessions.map(s => s.date.split('T')[0]))].sort();
  let best = 1, current = 1;
  for (let i = 1; i < dates.length; i++) {
    const prev = new Date(dates[i - 1]);
    const curr = new Date(dates[i]);
    const diff = (curr.getTime() - prev.getTime()) / 86400000;
    if (diff === 1) { current++; best = Math.max(best, current); }
    else current = 1;
  }
  return best;
}

export function averageRating(sessions: InterventionSession[]): number {
  if (sessions.length === 0) return 0;
  return sessions.reduce((s, x) => s + x.rating, 0) / sessions.length;
}